import * as turf from '@turf/turf'
import mapboxgl, { GeoJSONFeature, LngLat, MapMouseEvent } from 'mapbox-gl'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IGeoJson2DGeographicCoordinates,
  IRemoteTileset,
  ITileset,
  LocationType,
  MapFileType,
  SitemapItemShapeType,
} from '~/client/graph'
import { mapboxFeatureToAddress } from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import {
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
  MAPBOX_CLICK_TOLERANCE_PX,
  POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
  SHAPE_ARROW_LINE_LAYER,
  SHAPE_EDITABLE_POINTS_LAYER,
  SHAPE_FILL_LAYER,
  SHAPE_LINE_LAYER,
  SHAPE_SELECTED_POINTS_LAYER,
  TILESET_PREFIXES,
  TILESET_SOURCE,
  UNDER_PLAN_FILL_LAYER,
} from '~/client/src/shared/components/MapBoxViewer/mapboxConstants'
import {
  LOCATION_MARKERS_LAYER,
  locationMarkerLayerIds,
  mapViewItemKey,
} from '~/client/src/shared/components/MapBoxViewer/mapboxConstants'
import { getMapBoxPlaceFromCoordinates } from '~/client/src/shared/components/MapBoxViewer/mapboxGeocoding'
import MapBoxViewerStore, {
  ISitemapWithBasemap,
} from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import { MapFileOnGlobe } from '~/client/src/shared/components/SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { isBeyondOneRingShape } from '~/client/src/shared/components/SitemapHelpers/models/PolyLineShapeCoordinates'
import MapViewItemDrawnPart from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemDrawnPart'
import {
  hitsOffViewBadge,
  locationMarkerGrabLayerIds,
} from '~/client/src/shared/components/SitemapHelpers/utils/GLLocationMarkerLayer'
import {
  GRABBING_CURSOR,
  GRAB_CURSOR,
} from '~/client/src/shared/components/SitemapHelpers/utils/locationCursor'
import Keys from '~/client/src/shared/enums/Keys'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'
import GlobeView from '~/client/src/shared/models/GlobeView'
import ProjectLocation from '~/client/src/shared/models/LocationObjects/ProjectLocation'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import RemoteTilesetsStore from '~/client/src/shared/stores/domain/RemoteTilesets.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import GlobeViewSetupStore from '~/client/src/shared/stores/GlobeViewSetup.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import MapViewItemsSetupStore from './MapViewItemsSetup.store'
import RemoteTilesetsSetupStore from './RemoteTilesetsSetup.store'
import SitemapsSetupStore from './SitemapsSetup.store'
import TilesetsSetupStore from './TilesetsSetup.store'

const DEGREE_IN_RADIANS = Math.PI / 180
const CONSTRAIN_ANGLE_STEP = 45
const RUBBER_MODE_OPACITY = 50

const CROSSHAIR_CURSOR = 'url(/static/cursors/draw.png), crosshair'
const ADD_POINT_CURSOR = 'url(/static/cursors/add-point.png), copy'
const CLOSE_SHAPE_CURSOR = 'url(/static/cursors/close-shape.png), auto'
const DRAW_CURSORS = [CROSSHAIR_CURSOR, ADD_POINT_CURSOR, CLOSE_SHAPE_CURSOR]
const CURSOR_NE_ROTATE = 'url(/static/cursors/ne-rotate.png) 16 16, auto'
const CURSOR_NW_ROTATE = 'url(/static/cursors/nw-rotate.png) 16 16, auto'
const CURSOR_SE_ROTATE = 'url(/static/cursors/se-rotate.png) 16 16, auto'
const CURSOR_SW_ROTATE = 'url(/static/cursors/sw-rotate.png) 16 16, auto'

const SHOW_PLAN_STORAGE_KEY = 'show-plan-key'

const EDITOR_SHAPE_LAYER_IDS = [
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
]

const isOpenLine = (shape?: {
  type?: SitemapItemShapeType
  isClosed?: boolean
}): boolean => shape?.type === SitemapItemShapeType.Polyline && !shape.isClosed

export enum ViewType {
  Objects = 'Objects',
  MapFiles = 'Map Files',
}

// Fixed for the life of the module: the hit-test behind the grab cursor runs on every pointer
// sample, and rebuilding this array there was pure waste.
const LOCATION_MARKER_GRAB_LAYER_IDS = locationMarkerGrabLayerIds(
  LOCATION_MARKERS_LAYER,
)
// Without the outline: its quad spans the whole box, so grabbing may use it but deciding
// whether a CLICK landed on the object may not — see locationMarkerGrabLayerIds.
const LOCATION_MARKER_LAYER_IDS = locationMarkerLayerIds(LOCATION_MARKERS_LAYER)

export default class MapBoxEditorStore {
  @observable public selectedViewType: ViewType = ViewType.Objects
  @observable public isEditableItemInDragMode: boolean = false

  //map evens props
  @observable public selectedShapePointIdx: number = null
  public draggableShapePointIdx: number = null
  // Whether the gesture actually moved anything, so a press that only selected does not push a
  // no-op onto the undo stack.
  private hasMovedInDrag: boolean = false
  // What the gesture GRABBED, which is not which part is selected: adding a shape leaves the
  // Shape part selected, so without this a press on the pin moves only the polygon. Observable
  // because isMarkerDragging is a @computed over it — undecorated, MobX never tracks it and that
  // computed only invalidates on its other input.
  @observable private isMarkerGrab: boolean = false
  private lastRotationCoords: LngLat = null
  private pressedKey: Keys = null
  private isRotateMode: boolean = false
  public selectedMapViewItemOnStartMoving: MapViewItemBase = null
  public mouseDownCoordinates: LngLat = null
  private mouseDownPoint: MapMouseEvent['point'] = null

  @observable public opacity: number = 0
  // visibility on map during rubber sheeting
  @observable public leftPanelVisibility: {
    plans: {
      [attrId: string]: boolean
    }
    items: {
      [attrId: string]: boolean
    }
    tilesets: {
      [attrId: string]: boolean
    }
    remoteTilesets: {
      [attrId: string]: boolean
    }
  } = { plans: {}, items: {}, tilesets: {}, remoteTilesets: {} }

  @observable public isDraggingMode: boolean = false
  @observable public isRubberMode: boolean = false

  public constructor(
    private readonly mapBoxViewerStore: MapBoxViewerStore,
    private readonly mapViewItemsSetupStore: MapViewItemsSetupStore,
    private readonly sitemapsStore: SitemapsStore,
    private readonly remoteTilesetsStore: RemoteTilesetsStore,
    private readonly state: InitialState,
    protected readonly locationAttributesStore: LocationAttributesStore,
    public readonly globeViewSetupStore: GlobeViewSetupStore,
    public readonly basemapsStore: BasemapsStore,
    public readonly sitemapsSetupStore: SitemapsSetupStore,
    public readonly tilesetsSetupStore: TilesetsSetupStore,
    private readonly remoteTilesetsSetupStore: RemoteTilesetsSetupStore,
  ) {
    this.setDefaultValues()
    makeObservable(this)
  }

  @action.bound
  public onViewTypeSelect(viewType: ViewType): void {
    this.selectedViewType = viewType
  }

  @action.bound
  private deselectMapViewItems(): void {
    this.sitemapsSetupStore.deselectSitemap()
    this.tilesetsSetupStore.deselectTileset()
    this.remoteTilesetsSetupStore.deselectRemoteTileset()
    this.mapBoxViewerStore.tilesetsControlStore.resetTilesetFeaturesSelection()
    this.mapViewItemsSetupStore.safelyDeselectMapViewItem()
  }

  @action.bound
  public selectMapViewItem(item: MapViewItemBase): void {
    this.deselectMapViewItems()
    this.mapViewItemsSetupStore.selectMapViewItem(item)
    this.onViewTypeSelect(ViewType.Objects)
  }

  @action.bound
  public selectSitemapById(sitemapId: string): void {
    this.deselectMapViewItems()
    this.sitemapsSetupStore.selectSitemapById(sitemapId)
    this.onViewTypeSelect(ViewType.MapFiles)
  }

  @action.bound
  public selectTilesetFeatures(features: GeoJSONFeature[]): void {
    this.deselectMapViewItems()
    this.mapBoxViewerStore.tilesetsControlStore.selectedTilesetFeatures =
      features
    this.mapBoxViewerStore.tilesetsControlStore.selectedTilesetFeature =
      features.at(-1)
    this.onViewTypeSelect(ViewType.MapFiles)
  }

  public isSitemapItemSelected(item: MapViewItemBase): boolean {
    return this.mapViewItemsSetupStore.isMapViewItemSelected(item)
  }

  public get selectedGlobe(): GlobeView {
    return this.globeViewSetupStore.selectedGlobeView
  }

  @action.bound
  public setDefaultValues(): void {
    this.opacity = Number(localStorage.getItem(SHOW_PLAN_STORAGE_KEY))
  }

  @action.bound
  public toggleItemVisibility(itemId: string): void {
    this.leftPanelVisibility.items[itemId] =
      !this.leftPanelVisibility.items[itemId]
  }

  @action.bound
  public togglePlanVisibility(itemId: string): void {
    this.leftPanelVisibility.plans[itemId] =
      !this.leftPanelVisibility.plans[itemId]
  }

  @action.bound
  public toggleTilesetVisibility(itemId: string): void {
    this.leftPanelVisibility.tilesets[itemId] =
      !this.leftPanelVisibility.tilesets[itemId]
  }

  @action.bound
  public toggleRemoteTilesetVisibility(itemId: string): void {
    this.leftPanelVisibility.remoteTilesets[itemId] =
      !this.leftPanelVisibility.remoteTilesets[itemId]
  }

  @action.bound
  public async createMapImage(): Promise<string> {
    return this.mapBoxViewerStore.createMapImageDataUrl()
  }

  @action.bound
  public setOpacity(opacity: number): void {
    this.opacity = opacity
  }

  @action.bound
  public updateOpacityBySetupStep(): void {
    this.opacity = this.isDraggingMode
      ? RUBBER_MODE_OPACITY
      : Number(localStorage.getItem(SHOW_PLAN_STORAGE_KEY))
  }

  @action.bound
  public toggleOpacity(): void {
    this.opacity = this.isMapboxHidden ? 0 : 100
    localStorage.setItem(SHOW_PLAN_STORAGE_KEY, this.opacity.toString())
  }

  public get isMapboxHidden(): boolean {
    return this.opacity === 100
  }

  @action.bound
  public setLeftVisibility(): void {
    this.leftPanelVisibility = {
      plans: {},
      items: {},
      tilesets: {},
      remoteTilesets: {},
    }
    this.selectedGlobe.items.forEach(item => {
      this.leftPanelVisibility[item.sitemapItemId] = true
    })
  }

  @computed
  public get displayedGlobeItems(): MapViewItemBase[] {
    const items = this.isRubberMode
      ? this.mapBoxViewerStore.displayedGlobeViewItems.filter(
          item => this.leftPanelVisibility.items[item.id],
        )
      : this.mapBoxViewerStore.displayedGlobeViewItems

    // The selected item STAYS, rendered from the editor's working copy with a ring around it.
    // The substitution is what makes an edit visible at all: buildGlobeViewItems re-clones every
    // sitemap item on each recompute, so a drag or a colour change lives only on the draft and
    // would otherwise be thrown away by the next rebuild.
    const draft = this.mapViewItemsSetupStore.selectedMapViewItem
    if (!draft) {
      return items
    }

    // Known to the view: SUBSTITUTED. Unknown: APPENDED — an item being created lives only in
    // the editor until it is committed, so there is nothing to substitute for and without this
    // it stays invisible while being placed. Membership is tested against the FULL list, not the
    // rubber-filtered one, so a deliberately hidden item is not resurrected by being selected.
    const known = this.mapBoxViewerStore.displayedGlobeViewItems.some(
      item => item.id === draft.id,
    )
    return known
      ? items.map(item => (item.id === draft.id ? draft : item))
      : [...items, draft]
  }

  // The item's own setting for this view, not whether the map draws it: below the project-marker
  // zoom the map draws nothing, and every selection would read as hidden. An item being created
  // has no id yet, and is new rather than hidden.
  @computed
  public get isSelectedItemOffView(): boolean {
    const draft = this.mapViewItemsSetupStore.selectedMapViewItem
    return !!draft?.id && !draft.isDisplayed
  }

  @computed
  public get displayedSitemapWithBasemaps(): ISitemapWithBasemap[] {
    if (this.isRubberMode) {
      return this.sitemapsStore.plans
        .filter(p => !!this.leftPanelVisibility.plans[p.id])
        .map(p => ({
          sitemap: p,
          basemap: this.basemapsStore.byId.get(p.basemapId),
        }))
    }
    return this.mapBoxViewerStore.sitemapWithBasemapsOnGlobe
  }

  @computed
  public get displayedTilesets(): ITileset[] {
    if (this.isRubberMode) {
      return this.state.activeTilesetsList.filter(
        t => !!this.leftPanelVisibility.tilesets[t.id],
      )
    }
    return this.mapBoxViewerStore.tilesetsControlStore.globeTilesets
  }

  @computed
  public get displayedRemoteTilesets(): IRemoteTileset[] {
    if (this.isRubberMode) {
      return this.remoteTilesetsStore.list.filter(
        p => !!this.leftPanelVisibility.remoteTilesets[p.id],
      )
    }
    return this.mapBoxViewerStore.globeRemoteTilesets
  }

  @computed
  public get mapFilesOnGlobe(): MapFileOnGlobe[] {
    if (this.isRubberMode) {
      return [
        ...this.displayedSitemapWithBasemaps.map(
          s => ({ type: MapFileType.Sitemap, file: s }) as MapFileOnGlobe,
        ),
        ...this.displayedTilesets.map(
          t => ({ type: MapFileType.Tileset, file: t }) as MapFileOnGlobe,
        ),
        ...this.displayedRemoteTilesets.map(
          t => ({ type: MapFileType.RemoteTileset, file: t }) as MapFileOnGlobe,
        ),
      ]
    }
    return this.mapBoxViewerStore.mapFilesOnGlobe
  }

  @action.bound
  public onMapClick(e: MapMouseEvent): void {
    this.mapBoxViewerStore.clickCoords = e.lngLat
    this.selectedShapePointIdx = null
    this.mapBoxViewerStore.selectedMarkerItemId = null
    this.mapBoxViewerStore.tilesetsControlStore.resetTilesetFeaturesSelection()
    const selectedMapViewItem = this.mapViewItemsSetupStore.selectedMapViewItem
    const shapeFeature = this.innermostShapeFeature(
      this.hitFeatures(e)?.filter(f =>
        [
          SHAPE_FILL_LAYER,
          SHAPE_LINE_LAYER,
          ...EDITOR_SHAPE_LAYER_IDS,
        ].includes(f?.layer?.id),
      ),
    )
    const shapeEditablePointsFeature = e.features?.find(
      f => f?.layer?.id === SHAPE_EDITABLE_POINTS_LAYER,
    )
    const polylineAdditionalEditablePointFeature = e.features?.find(
      f => f?.layer?.id === POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
    )
    const sitemapFeature = e.features?.find(
      f => f?.layer?.id === UNDER_PLAN_FILL_LAYER,
    )
    let tilesetShapeFeatures = e.features
      ?.filter(f =>
        TILESET_PREFIXES.some(prefix => f?.layer?.id.includes(prefix)),
      )
      .filter((v, i, a) => a.findIndex(f => f.id === v.id) === i)
    // Because the mapbox divides the features tileset into parts, we only have a part of the geometry
    if (tilesetShapeFeatures?.length) {
      // Here we get the full features with all the geometry
      tilesetShapeFeatures = tilesetShapeFeatures.map(f =>
        f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
          ? this.getTilesetFeature(f)
          : f,
      )
    }
    switch (true) {
      case this.isOffViewBadgeAt(e.point):
        this.mapViewItemsSetupStore.showSelectedMapViewItem()
        break
      case this.isEditingPolyline &&
        !!shapeEditablePointsFeature &&
        shapeEditablePointsFeature.properties.idx === 0 &&
        !selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed:
        selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed = true
        break
      case !!shapeEditablePointsFeature:
        this.selectedShapePointIdx = shapeEditablePointsFeature.properties.idx
        break
      case this.isEditingPolyline && !!shapeEditablePointsFeature:
        break
      case !!polylineAdditionalEditablePointFeature:
        selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates = [
          ...selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates.slice(
            0,
            polylineAdditionalEditablePointFeature.properties.idx + 1,
          ),
          { latitude: e.lngLat.lat, longitude: e.lngLat.lng },
          ...selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates.slice(
            polylineAdditionalEditablePointFeature.properties.idx + 1,
          ),
        ]
        break
      // A click on the line itself or its own label is aimed at the line, e.g. double-clicking
      // into the Label part, not at a new vertex.
      case this.isEditingPolyline &&
        (!!this.selectedMarkerFeatureAt(e.point) ||
          (this.isOpenLineSelected && !!this.ownShapeBodyIn(e.features))):
        break
      case this.isEditingPolyline &&
        !selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed &&
        !this.hitsAnotherLocationMarker(e.point) &&
        e.originalEvent.shiftKey:
        break
      case this.isEditingPolyline &&
        !selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed &&
        !this.hitsAnotherLocationMarker(e.point):
        selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates.push({
          longitude: e.lngLat.lng,
          latitude: e.lngLat.lat,
        })
        break
      // The marker layers bind their own click, and react-map-gl binds the viewer's at map
      // creation — so this runs first. A marker click has to stop here for the layer's handler
      // to own it: falling through reaches the sitemap branch, whose selectSitemapById also
      // deselects, so the save prompt comes back for a marker standing on a plan sheet. Ahead
      // of the shape cases: a marker over another item's shape belongs to its own item.
      case !!this.locationMarkerFeatureAt(e.point):
        break
      case e?.features?.length &&
        !!shapeFeature &&
        !this.mapBoxViewerStore.selectedMarkerItemId:
        const itemId = shapeFeature.properties?.itemId
        const currentItemId =
          this.mapViewItemsSetupStore.selectedMapViewItem?.id
        if (itemId && (!currentItemId || currentItemId !== itemId)) {
          this.selectMapViewItem(
            this.displayedGlobeItems.find(
              i => i.id === shapeFeature?.properties?.itemId,
            ),
          )
        }
        break
      case !!selectedMapViewItem:
        this.mapViewItemsSetupStore.safelyDeselectMapViewItem()
        break
      case !!sitemapFeature:
        this.selectSitemapById(sitemapFeature?.properties?.sitemapId)
        break
      case e?.features?.length && !!tilesetShapeFeatures.length:
        this.selectTilesetFeatures(tilesetShapeFeatures)
        break
      case !this.isRubberMode:
        this.sitemapsSetupStore.deselectSitemap()
        this.tilesetsSetupStore.deselectTileset()
        this.remoteTilesetsSetupStore.deselectRemoteTileset()
        break
    }
    this.mapBoxViewerStore.selectedMarkerItemId = null
  }

  // Finds all tile fragments with the same id and merges them into the full shape. Tile clipping
  // can produce degenerate fragments (e.g. a ring with < 4 positions), so build features from each
  // fragment's actual geometry — turf.feature does not validate, unlike turf.polygon, which threw
  // "Each LinearRing ... must have 4 or more Positions" and blocked selection (STX1-806) — and
  // preserve each part's real type so a MultiPolygon reassembles instead of being cast to Polygon.
  private getTilesetFeature(feature: GeoJSONFeature): any {
    if (feature.id === undefined || feature.id === null) {
      return feature
    }
    const parts = this.mapBoxViewerStore.mapRef
      .getMap()
      .querySourceFeatures(feature.source, {
        sourceLayer: feature.sourceLayer,
        filter: ['==', ['id'], feature.id],
      })
      .map(f => f.geometry)
      .filter(
        (g): g is GeoJSON.Polygon | GeoJSON.MultiPolygon =>
          g?.type === 'Polygon' || g?.type === 'MultiPolygon',
      )

    if (!parts.length) {
      return feature
    }

    try {
      const merged = turf.union(
        turf.featureCollection(parts.map(g => turf.feature(g))),
      )
      if (merged) {
        feature.geometry = merged.geometry
      }
    } catch {
      // A degenerate clipped fragment can still make the union throw; keep the clicked feature's
      // own geometry rather than crashing selection (STX1-806).
    }

    return feature
  }

  private getConstrainedPoint(
    point: LngLat,
    basePoint: IGeoJson2DGeographicCoordinates,
    bearing: number = 0,
  ): IGeoJson2DGeographicCoordinates {
    const cosLat = Math.cos(basePoint.latitude * DEGREE_IN_RADIANS)

    const dx = (point.lng - basePoint.longitude) * cosLat
    const dy = point.lat - basePoint.latitude

    const mercatorAngle = Math.atan2(dy, dx) / DEGREE_IN_RADIANS

    const screenAngle = mercatorAngle + bearing
    const constrainedScreenAngle =
      Math.round(screenAngle / CONSTRAIN_ANGLE_STEP) * CONSTRAIN_ANGLE_STEP
    const constrainedMercatorAngle = constrainedScreenAngle - bearing

    const distance = Math.sqrt(dx * dx + dy * dy)

    const newDx =
      distance * Math.cos(constrainedMercatorAngle * DEGREE_IN_RADIANS)
    const newDy =
      distance * Math.sin(constrainedMercatorAngle * DEGREE_IN_RADIANS)

    return {
      longitude: basePoint.longitude + newDx / cosLat,
      latitude: basePoint.latitude + newDy,
    }
  }

  @action.bound
  public onMapDblClick(e: MapMouseEvent): void {
    const features = this.hitFeatures(e)
    if (features?.length) {
      const shapeEditorFeature = features.find(f =>
        [
          SHAPE_FILL_LAYER,
          SHAPE_LINE_LAYER,
          SHAPE_SELECTED_POINTS_LAYER,
          ...EDITOR_SHAPE_LAYER_IDS,
        ].includes(f?.layer?.id),
      )
      this.onMapClick(e)
      // The marker layer's dblclick picks Label or Icon here; selecting Shape too would race it.
      if (shapeEditorFeature && !this.locationMarkerFeatureAt(e.point)) {
        this.mapViewItemsSetupStore.selectSitemapItemDrawnPart(
          MapViewItemDrawnPart.Shape,
        )
      }
      e.originalEvent.stopPropagation()
    }
  }

  @action.bound
  public onMouseDown(e: MapMouseEvent): void {
    this.selectedShapePointIdx = null
    this.mouseDownCoordinates = e.lngLat
    this.mouseDownPoint = e.point

    const { selectedMapViewItem } = this.mapViewItemsSetupStore
    if (
      this.isEditingPolyline &&
      e.originalEvent.shiftKey &&
      !this.hitsAnotherLocationMarker(e.point)
    ) {
      e.preventDefault()
      e.originalEvent.stopPropagation()
      const { coordinates } = selectedMapViewItem.sitemapItem.shapeCoordinates
      const prevPoint = coordinates[coordinates.length - 1]
      const bearing = this.mapBoxViewerStore.viewport?.bearing || 0
      const point = this.getConstrainedPoint(
        this.mouseDownCoordinates,
        prevPoint,
        bearing,
      )
      this.mapViewItemsSetupStore.selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates.push(
        point,
      )
    }
    const shapeEditorFeature = e.features?.find(
      f => f?.layer?.id === SHAPE_EDITABLE_POINTS_LAYER,
    )
    // Drawn in every part, but only the Shape part drags the shape alone. A midpoint sits on an
    // open line's stroke and inserts a vertex, so pressing it never grabs the line.
    const shapeFillFeature =
      !e.features?.some(
        f => f?.layer?.id === POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
      ) && this.ownShapeBodyIn(e.features)
    // The badge sits on the ring, which grabs, but a press on it is a click.
    const markerFeature = this.isOffViewBadgeAt(e.point)
      ? undefined
      : this.selectedMarkerFeatureAt(e.point)
    switch (true) {
      case this.pressedKey === Keys.Ctrl &&
        !!selectedMapViewItem &&
        !this.isMarkerPartSelected &&
        !!selectedMapViewItem.globeViewItemProperties?.shapeProperties
          ?.isDisplayed:
        e.preventDefault()
        this.isRotateMode = true
        this.lastRotationCoords = this.mouseDownCoordinates
        break
      case e?.features?.length && !!shapeEditorFeature:
        e.preventDefault()
        const idx = shapeEditorFeature?.properties?.idx as number
        if (selectedMapViewItem && idx !== null) {
          this.draggableShapePointIdx = idx
          this.selectedShapePointIdx = idx
          this.isEditableItemInDragMode = true
        }
        break
      case !!markerFeature || (e?.features?.length && !!shapeFillFeature):
        e.preventDefault()
        this.isMarkerGrab = !!markerFeature
        this.isEditableItemInDragMode = true
        this.selectedMapViewItemOnStartMoving = selectedMapViewItem.copy()
        break
    }
    if (this.isEditableItemInDragMode || this.isRotateMode) {
      this.armWindowDragRelease()
    }
  }

  // The map's mouseup fires only for a release over the canvas, so releasing outside leaves the
  // gesture armed and the next move drags the object with no button held. The DOM marker this
  // replaced captured the pointer; a GL drag has to listen for the release itself.
  private endDragFromWindow: (() => void) | null = null

  private armWindowDragRelease(): void {
    if (this.endDragFromWindow) {
      return
    }
    const end = (): void => this.onMouseUp()
    this.endDragFromWindow = end
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    // Alt-tab mid-drag: the release lands in another window and never reaches this one.
    window.addEventListener('blur', end)
  }

  // Unmounting mid-drag is not a completed edit: the window listeners retain this store and a
  // later release would run onMouseUp against a dead editor, pushing history and reverse
  // geocoding after teardown. So this drops the gesture WITHOUT those side effects, and puts
  // the fade back before the map goes.
  @action.bound
  public cancelDrag(): void {
    this.disarmWindowDragRelease()
    this.useImmediateSymbolPlacement(false)
    this.draggableShapePointIdx = null
    this.isEditableItemInDragMode = false
    this.isMarkerGrab = false
    this.hasMovedInDrag = false
    this.isRotateMode = false
    this.selectedMapViewItemOnStartMoving = null
  }

  private disarmWindowDragRelease(): void {
    if (!this.endDragFromWindow) {
      return
    }
    window.removeEventListener('pointerup', this.endDragFromWindow)
    window.removeEventListener('pointercancel', this.endDragFromWindow)
    window.removeEventListener('blur', this.endDragFromWindow)
    this.endDragFromWindow = null
  }

  // Reachable twice for one release, since both the map's mouseup and the window listener fire
  // over the canvas — so this is idempotent: the second pass adds no history entry.
  @action.bound
  public onMouseUp(): void {
    this.disarmWindowDragRelease()
    // Both of these belonged to the DOM marker's drag-end and lost their caller when the marker
    // was replaced by the GL layers: without them a completed drag leaves no undo entry, and a
    // linked project location keeps the address of the place it was dragged away from.
    const movedItem = this.hasMovedInDrag
      ? this.mapViewItemsSetupStore.selectedMapViewItem
      : null
    // Whether the PIN moved, which is not the same as whether the gesture moved something.
    // moveShapePoints and the shape-fill branch of moveItem both set hasMovedInDrag while
    // deliberately leaving sitemapItem.coordinates alone, so keying the address sync on it
    // reverse-geocoded an unchanged position on every shape edit — a billable request, and a
    // newProjectAddress write that then made hasUnsavedGlobeChanges raise the save-confirm
    // dialog for an edit that never touched the address.
    const start =
      this.selectedMapViewItemOnStartMoving?.sitemapItem?.coordinates
    const end = movedItem?.sitemapItem?.coordinates
    const hasPinMoved =
      !!start &&
      !!end &&
      (start.latitude !== end.latitude || start.longitude !== end.longitude)

    this.draggableShapePointIdx = null
    this.isEditableItemInDragMode = false
    this.isMarkerGrab = false
    this.hasMovedInDrag = false
    this.selectedMapViewItemOnStartMoving = null
    this.isRotateMode = false
    this.useImmediateSymbolPlacement(false)

    if (movedItem) {
      // History covers any edit; only the address is pin-specific.
      this.mapViewItemsSetupStore.addCurrentStateToHistory()
      if (hasPinMoved) {
        this.syncLinkedProjectAddress(movedItem)
      }
    }
  }

  // Only a project location that is linked to its address: for everything else the address is
  // independent of where the pin sits.
  private syncLinkedProjectAddress(item: MapViewItemBase): void {
    const { dataObject, sitemapItem } = item
    const coordinates = sitemapItem?.coordinates
    if (
      !coordinates ||
      !dataObject?.is(LocationType.ProjectLocation) ||
      !(dataObject as ProjectLocation).isLinked
    ) {
      return
    }
    getMapBoxPlaceFromCoordinates(
      new LngLat(coordinates.longitude, coordinates.latitude),
      mapboxgl.accessToken,
    )
      .then(place =>
        runInAction(() => {
          this.mapViewItemsSetupStore.newProjectAddress =
            mapboxFeatureToAddress(place)
        }),
      )
      // A failed reverse geocode leaves the previous address; it must not break the drop.
      .catch(() => undefined)
  }

  // mapbox gates symbol re-placement on the fade window: a new placement only starts once
  // `commitTime + fadeDuration` is in the past. At the default a dragged symbol therefore sits
  // still and then jumps, and its PREVIOUS placement fades out where it was — the ghost
  // trailing the circle and the label. icon-allow-overlap exempts the outline from both.
  //
  // Zero selects mapbox's immediate-placement branch instead, which re-places every frame and
  // skips the fade. Scoped to the gesture: with zero, placement also re-runs on every frame the
  // camera moves, and bounding that is exactly what the fade window is for.
  //
  // fadeDuration is a Map constructor option with no setter, but it is read fresh on every
  // render, so this is the only way to change it for the duration of a drag.
  private fadeDurationBeforeDrag: number = null

  private useImmediateSymbolPlacement(isDragging: boolean): void {
    const map = this.mapBoxViewerStore.mapRef?.getMap() as unknown as {
      _fadeDuration?: number
      triggerRepaint?: () => void
    }
    if (!map) {
      return
    }
    if (isDragging) {
      if (this.fadeDurationBeforeDrag === null) {
        this.fadeDurationBeforeDrag = map._fadeDuration
        map._fadeDuration = 0
      }
      return
    }
    if (this.fadeDurationBeforeDrag !== null) {
      map._fadeDuration = this.fadeDurationBeforeDrag
      this.fadeDurationBeforeDrag = null
      map.triggerRepaint?.()
    }
  }

  @action.bound
  public onMouseMove(e: MapMouseEvent): void {
    const selectedMapViewItem = this.mapViewItemsSetupStore.selectedMapViewItem
    if (
      (this.isEditableItemInDragMode || this.isRotateMode) &&
      selectedMapViewItem
    ) {
      this.useImmediateSymbolPlacement(true)
    } else if (this.fadeDurationBeforeDrag !== null) {
      // A gesture that ends anywhere but on the map — released outside the window, or
      // interrupted by an unmount — never reaches onMouseUp, and a fade left at zero makes
      // every later pan re-place every symbol for the rest of the session, with no visible
      // cause. The next pointer move over the map puts it back.
      this.useImmediateSymbolPlacement(false)
    }
    if (
      this.isEditableItemInDragMode &&
      !!selectedMapViewItem &&
      this.isPastClickTolerance(e.point)
    ) {
      if (this.draggableShapePointIdx !== null) {
        this.moveShapePoints(e.lngLat)
      } else {
        this.moveItem(e.lngLat)
      }
    }
    if (
      this.isRotateMode &&
      !!selectedMapViewItem &&
      this.isPastClickTolerance(e.point)
    ) {
      selectedMapViewItem.sitemapItem.shapeCoordinates.rotate(
        this.lastRotationCoords,
        e.lngLat,
      )
      this.lastRotationCoords = e.lngLat
      // Rotation edits shapeCoordinates, so it needs the history entry that onMouseUp gates on
      // this flag; the address sync behind it stays keyed on sitemapItem.coordinates, unmoved.
      this.hasMovedInDrag = true
    }
    const shapeEditablePointFeature = e.features?.find(
      f => f?.layer?.id === SHAPE_EDITABLE_POINTS_LAYER,
    )
    const polylineAdditionalEditablePointFeature = e.features?.find(
      f => f?.layer?.id === POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
    )
    const shapeFeature = e.features?.find(f =>
      [SHAPE_FILL_LAYER, SHAPE_LINE_LAYER, SHAPE_ARROW_LINE_LAYER].includes(
        f?.layer?.id,
      ),
    )
    const sitemapFeature = e.features?.find(
      f => f?.layer?.id === UNDER_PLAN_FILL_LAYER,
    )

    const tilesetFeature = e.features?.find(f =>
      TILESET_PREFIXES.some(prefix => f?.layer?.id.includes(prefix)),
    )

    const selectedShapeFillFeature = this.ownShapeBodyIn(e.features)

    switch (true) {
      // A drag owns the cursor for the whole gesture. Letting what is under the pointer answer
      // made it flicker: the object being dragged passes under the cursor continuously.
      case this.isDraggingSelection:
        this.mapBoxViewerStore.mapCursor = GRABBING_CURSOR
        break
      case this.isOffViewBadgeAt(e.point):
        this.mapBoxViewerStore.mapCursor = 'pointer'
        break
      case this.isEditingPolyline &&
        !!shapeEditablePointFeature &&
        shapeEditablePointFeature.properties.idx === 0 &&
        !selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed:
        this.mapBoxViewerStore.mapCursor = CLOSE_SHAPE_CURSOR
        break
      case this.isEditingPolyline && !!shapeEditablePointFeature:
        this.mapBoxViewerStore.mapCursor = CROSSHAIR_CURSOR
        break
      case (this.isEditingPolyline &&
        !selectedMapViewItem.sitemapItem?.shapeCoordinates?.isClosed &&
        !selectedShapeFillFeature &&
        !this.hitsAnotherLocationMarker(e.point)) ||
        !!polylineAdditionalEditablePointFeature:
        this.mapBoxViewerStore.mapCursor = ADD_POINT_CURSOR
        break
      case this.pressedKey === Keys.Ctrl &&
        !!selectedMapViewItem &&
        !this.isMarkerPartSelected &&
        selectedMapViewItem.sitemapItem?.shapeCoordinates?.type !==
          SitemapItemShapeType.Circle:
        this.mapBoxViewerStore.mapCursor = this.getRotationCursor(e.lngLat)
        break
      // The selected object is something you move, so it shows that it can be grabbed. The
      // pointer below is for unselected objects only.
      case !!selectedShapeFillFeature ||
        !!this.selectedMarkerFeatureAt(e.point):
        this.mapBoxViewerStore.mapCursor = GRAB_CURSOR
        break
      case !!this.hitFeatures(e)?.length:
        this.mapBoxViewerStore.mapCursor = 'pointer'
        break
      default:
        this.mapBoxViewerStore.mapCursor = null
    }

    if (shapeFeature) {
      this.mapBoxViewerStore.hoveredShapeItemId =
        shapeFeature.properties?.itemId || null
      this.mapBoxViewerStore.hoveredSitemapId = null
    } else {
      this.mapBoxViewerStore.hoveredShapeItemId = null
      if (sitemapFeature) {
        this.handleSitemapFeatureClicK(sitemapFeature, tilesetFeature)
      } else {
        this.mapBoxViewerStore.hoveredSitemapId = null
      }
    }
  }

  private handleSitemapFeatureClicK(
    sitemapFeature: GeoJSONFeature,
    tilesetFeature: GeoJSONFeature,
  ) {
    const sitemapIdx = this.mapFilesOnGlobe.findIndex(
      f =>
        f.type === MapFileType.Sitemap &&
        (f.file as ISitemapWithBasemap)?.sitemap?.id ===
          sitemapFeature.properties?.sitemapId,
    )
    const tilesetIdx = tilesetFeature
      ? this.mapFilesOnGlobe.findIndex(
          f =>
            f.type === MapFileType.Tileset &&
            (f.file as ITileset)?.id ===
              tilesetFeature.source.slice(TILESET_SOURCE.length),
        )
      : -1
    if (
      !this.mapBoxViewerStore.hoveredMarkerItemId &&
      sitemapIdx > tilesetIdx
    ) {
      this.mapBoxViewerStore.hoveredSitemapId =
        sitemapFeature.properties?.sitemapId || null
    } else {
      this.mapBoxViewerStore.hoveredSitemapId = null
    }
  }

  private getRotationCursor(cursorPosition: LngLat): string {
    const shapeCoords =
      this.mapViewItemsSetupStore.selectedMapViewItem.sitemapItem
        .shapeCoordinates
    const lngDif = shapeCoords.centerPoint[0] - cursorPosition.lng
    const latDif = shapeCoords.centerPoint[1] - cursorPosition.lat
    if (latDif >= 0) {
      if (lngDif >= 0) {
        return CURSOR_SW_ROTATE
      }
      if (lngDif < 0) {
        return CURSOR_SE_ROTATE
      }
    }
    if (latDif < 0) {
      if (lngDif >= 0) {
        return CURSOR_NW_ROTATE
      }
      if (lngDif < 0) {
        return CURSOR_NE_ROTATE
      }
    }
  }

  // A click inside a zone also hits the building around it; the zone is the one meant. Only a
  // shape lying wholly inside the pick takes over, so partly overlapping shapes keep the
  // bottom-most hit as before, and in the Shape part the item's own shape as before. An open
  // line is only hit on its stroke, the most precise target.
  private innermostShapeFeature(
    features: MapMouseEvent['features'],
  ): GeoJSONFeature | undefined {
    const stroke = features?.find(f =>
      isOpenLine(this.shapeOf(f.properties?.itemId)),
    )
    if (stroke) {
      return stroke
    }
    return features?.reduce<GeoJSONFeature | undefined>(
      (innermost, feature) =>
        innermost && this.isShapeWithin(feature, innermost)
          ? feature
          : innermost,
      this.ownShapeBodyIn(features) ?? features.at(-1),
    )
  }

  private isShapeWithin(inner: GeoJSONFeature, outer: GeoJSONFeature): boolean {
    const innerShape = this.shapeOf(inner.properties?.itemId)
    const outerShape = this.shapeOf(outer.properties?.itemId)
    if (
      !innerShape?.coordinates?.length ||
      !outerShape?.coordinates?.length ||
      innerShape === outerShape
    ) {
      return false
    }
    // booleanWithin takes no MultiPolygon as its first argument, so a multi-part shape is within
    // when every part is.
    const innerGeometry = innerShape.getShapePolygonFeature({}).geometry
    const innerPolygons: GeoJSON.Polygon[] =
      innerGeometry.type === 'MultiPolygon'
        ? innerGeometry.coordinates.map(coordinates => ({
            type: 'Polygon',
            coordinates,
          }))
        : [innerGeometry]
    const outerFeature = outerShape.getShapePolygonFeature({})
    try {
      return innerPolygons.every(polygon =>
        turf.booleanWithin(polygon, outerFeature),
      )
    } catch {
      return false
    }
  }

  // By key: an item being created has no id, so its own fill carries none either.
  private shapeOf(
    itemId: string,
  ): MapViewItemBase['sitemapItem']['shapeCoordinates'] {
    return this.displayedGlobeItems.find(
      i => mapViewItemKey(i.id) === mapViewItemKey(itemId),
    )?.sitemapItem?.shapeCoordinates
  }

  // The own outline keeps the item selected in the other parts; in the Shape part only the fill
  // counts, as before the outline was clickable. An open line has no fill, so its stroke does.
  private hitFeatures(e: MapMouseEvent): MapMouseEvent['features'] {
    return this.isShapePartSelected && !this.isOpenLineSelected
      ? e.features?.filter(f => f?.layer?.id !== ITEM_EDITOR_SHAPE_LINE_LAYER)
      : e.features
  }

  private ownShapeBodyIn(
    features: MapMouseEvent['features'],
  ): GeoJSONFeature | undefined {
    if (!this.isShapePartSelected) {
      return undefined
    }
    const bodyLayerIds = this.isOpenLineSelected
      ? EDITOR_SHAPE_LAYER_IDS
      : [ITEM_EDITOR_SHAPE_FILL_LAYER]
    return features?.find(f => bodyLayerIds.includes(f?.layer?.id))
  }

  private get isOpenLineSelected(): boolean {
    return isOpenLine(
      this.mapViewItemsSetupStore.selectedMapViewItem?.sitemapItem
        ?.shapeCoordinates,
    )
  }

  // The Label and Icon parts place the marker apart from its shape, which stays put.
  private get isMarkerPartSelected(): boolean {
    const { selectedMapViewItemDrawnPart } = this.mapViewItemsSetupStore
    return (
      selectedMapViewItemDrawnPart === MapViewItemDrawnPart.Label ||
      selectedMapViewItemDrawnPart === MapViewItemDrawnPart.Icon
    )
  }

  private get isShapePartSelected(): boolean {
    return (
      this.mapViewItemsSetupStore.selectedMapViewItemDrawnPart ===
      MapViewItemDrawnPart.Shape
    )
  }

  // A shape with several parts or a hole has no vertex editing: every vertex gesture would reach
  // the first ring of its first part only.
  private get isEditingPolyline(): boolean {
    const selectedMapViewItem = this.mapViewItemsSetupStore.selectedMapViewItem
    return (
      !!selectedMapViewItem &&
      this.isShapePartSelected &&
      !isBeyondOneRingShape(selectedMapViewItem.sitemapItem.shapeCoordinates) &&
      (selectedMapViewItem.sitemapItem.shapeCoordinates?.type ===
        SitemapItemShapeType.Polyline ||
        selectedMapViewItem.sitemapItem?.type === MapViewItemType.Line)
    )
  }

  // Clear only ours: the same slot also carries still-accurate hover cursors.
  @action.bound
  public resetDrawCursor(): void {
    if (DRAW_CURSORS.includes(this.mapBoxViewerStore.mapCursor)) {
      this.mapBoxViewerStore.mapCursor = null
    }
  }

  // On a saved line another location's marker selects it, while its surface stays drawable like
  // during creation. A line being created places points anywhere.
  private hitsAnotherLocationMarker(point: MapMouseEvent['point']): boolean {
    const selected = this.mapViewItemsSetupStore.selectedMapViewItem
    if (!selected?.id) {
      return false
    }
    const marker = this.locationMarkerFeatureAt(point) as
      | GeoJSONFeature
      | undefined
    return !!marker && marker.properties?.itemId !== selected.id
  }

  private isOffViewBadgeAt(point: MapMouseEvent['point']): boolean {
    if (!this.isSelectedItemOffView) {
      return false
    }
    const map = this.mapBoxViewerStore.mapRef?.getMap()
    return (
      !!map &&
      hitsOffViewBadge(
        map,
        LOCATION_MARKERS_LAYER,
        point,
        this.mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled,
      )
    )
  }

  // Queried rather than read off e.features: the marker layers are deliberately NOT in
  // interactiveLayerIds — they report hover through their own probe — so mapbox never puts them
  // in the event, and adding them there would double up that hover.
  // Any location marker, not just the selected one: a click has to be recognised as a marker
  // click before it is known which item it belongs to.
  private locationMarkerFeatureAt(point: MapMouseEvent['point']): unknown {
    const map = this.mapBoxViewerStore.mapRef?.getMap()
    if (!map) {
      return undefined
    }
    return map
      .queryRenderedFeatures(point, {
        layers: LOCATION_MARKER_LAYER_IDS.filter(id => map.getLayer(id)),
      })
      ?.find(f => !!f?.properties?.itemId)
  }

  private selectedMarkerFeatureAt(point: MapMouseEvent['point']): unknown {
    const selected = this.mapViewItemsSetupStore.selectedMapViewItem
    // The KEY, not the id: an item being placed has no id yet, and without this the hit-test
    // bails here, so the click falls through to the generic select path and prompts instead of
    // grabbing the item the admin just put down.
    const selectedId = selected ? mapViewItemKey(selected.id) : undefined
    const map = this.mapBoxViewerStore.mapRef?.getMap()
    if (!selectedId || !map) {
      return undefined
    }
    return map
      .queryRenderedFeatures(point, {
        // Rebuilt per call until this ran on every pointer sample of onMouseMove; the id set
        // is fixed, so it is built once. The getLayer filter has to stay — queryRenderedFeatures
        // throws on an id the style does not have.
        layers: LOCATION_MARKER_GRAB_LAYER_IDS.filter(id => map.getLayer(id)),
      })
      ?.find(f => f?.properties?.itemId === selectedId)
  }

  // Rotation is its own gesture with its own affordance, so it keeps its own cursor. Derived
  // rather than a second observable: the state it wants already exists here, and as its own flag
  // it silently sat false once the DOM marker's drag handlers — its only writer — were removed.
  @computed
  public get isMarkerDragging(): boolean {
    return !!this.isEditableItemInDragMode && this.isMarkerGrab
  }

  private get isDraggingSelection(): boolean {
    return (
      !this.isRotateMode &&
      (!!this.isEditableItemInDragMode || this.draggableShapePointIdx !== null)
    )
  }

  private isPastClickTolerance(point: MapMouseEvent['point']): boolean {
    return (
      this.hasMovedInDrag ||
      !point ||
      !this.mouseDownPoint ||
      point.dist(this.mouseDownPoint) >= MAPBOX_CLICK_TOLERANCE_PX
    )
  }

  @action.bound
  private moveItem(moveCoords: LngLat): void {
    const selectedMapViewItem = this.mapViewItemsSetupStore.selectedMapViewItem
    this.hasMovedInDrag = true
    const lngDif = (this.mouseDownCoordinates as LngLat)?.lng - moveCoords.lng
    const latDif = (this.mouseDownCoordinates as LngLat)?.lat - moveCoords.lat
    // Grabbing the shape fill while the Shape part is selected still moves the shape alone,
    // which is the point of that mode. Grabbing the pin has to move the location.
    if (this.isMarkerGrab || !this.isShapePartSelected) {
      selectedMapViewItem.sitemapItem.coordinates = {
        latitude:
          this.selectedMapViewItemOnStartMoving.sitemapItem.coordinates
            .latitude - latDif,
        longitude:
          this.selectedMapViewItemOnStartMoving.sitemapItem.coordinates
            .longitude - lngDif,
      }
    }
    // Optional: a marker with no shape has a null shapeCoordinates, and this threw on every
    // pointer frame of its drag — after the coordinates above were already assigned, so the
    // object still followed the cursor and only the rest of onMouseMove was lost. That is
    // where the grabbing cursor is chosen, which is how a null deref read as a cursor bug.
    const startShape =
      this.selectedMapViewItemOnStartMoving.sitemapItem.shapeCoordinates
    if (!this.isMarkerPartSelected && startShape) {
      const movedShape = startShape.copy()
      movedShape.translate(-latDif, -lngDif)
      selectedMapViewItem.sitemapItem.shapeCoordinates = movedShape
    }
  }

  @action.bound
  private moveShapePoints(moveCoords: LngLat): void {
    const selectedMapViewItem = this.mapViewItemsSetupStore.selectedMapViewItem
    this.hasMovedInDrag = true
    if (selectedMapViewItem.sitemapItem.shapeCoordinates?.coordinates?.length) {
      selectedMapViewItem.sitemapItem.shapeCoordinates.moveShapePoint(
        this.draggableShapePointIdx,
        moveCoords,
      )
    }
  }

  @action.bound
  public onKeyDown(event: KeyboardEvent): void {
    this.pressedKey = event.ctrlKey ? Keys.Ctrl : (event.key as Keys) || null

    if (
      this.selectedShapePointIdx !== null &&
      this.mapViewItemsSetupStore.selectedMapViewItem?.sitemapItem
        ?.shapeCoordinates?.type === SitemapItemShapeType.Polyline &&
      event.key === 'Delete'
    ) {
      this.mapViewItemsSetupStore.selectedMapViewItem.sitemapItem.shapeCoordinates.coordinates.splice(
        this.selectedShapePointIdx,
        1,
      )
      this.selectedShapePointIdx = null
    }
  }

  @action.bound
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onKeyUp(event: KeyboardEvent) {
    this.pressedKey = null
  }
}
